import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Button,
  Pagination,
  TextField,
  MenuItem,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import { BASE_URL } from "../../../services/configUrls";
import { useCallback } from "react";

// Common API FOR FDP AND TECHCAMP

const API_MAP = {
  fdp: {
    availability: "/event/institute/month-availability",
    past: "/event/institute/past-history",
    submit: "/event/institute/submit-interest",
  },
  tech_camp: {
    availability: "/event/institute/month-availability-techcamp",
    past: "/event/institute/past-history-techcamp",
    submit: "/event/institute/submit-interest-techcamp",
  },
};
/* ---------- Domain Row ---------- */
const DomainRow = ({ domain, week, onSubmit, loading, showDivider, onBookedClick }) => {
  const { status } = domain;
  const ACTION_WIDTH = 110;

  const STATUS_CONFIG = {
  accepted: {
    label: "ACCEPTED",
    bg: "#E3F2FD",
    color: "#1976d2",
  },
  inprogress: { label: "INPROGRESS", bg: "#FFF3E0", color: "#EF6C00" },
  booked: { label: "BOOKED", bg: "#FDECEA", color: "#D32F2F" },
  completed: { label: "COMPLETED", bg: "#E8F5E9", color: "#2E7D32" },
  cancelled: { label: "CANCELLED", bg: "#E2E3E5", color: "#383D41" },
  payment_pending: { label: 'PAYMENT PENDING', bg: '#FFF8E1', color: '#F57F17' },
  paid: { label: 'PAID', bg: '#E8F5E9', color: '#2E7D32' },
};

  const isAvailable = status === "available";
  const isDisabledInterest = status === "disabled";
  const isBooked = status === "booked";
  const isPaymentPending = status === "payment_pending";
  const isPaid = status === "paid";



  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: showDivider ? "1px solid #E5E7EB" : "none",
      }}
    >
      <Typography
        sx={{
          maxWidth: 260,
          fontSize: 14,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {domain.domain_name}
      </Typography>

      {isAvailable || isDisabledInterest ? (
        <Button
  size="small"
  variant="contained"
  disableElevation
  disableRipple
  disabled={isDisabledInterest || loading}
  onClick={isDisabledInterest ? undefined : onSubmit}
  sx={{
    width: ACTION_WIDTH,
    height: 32,
    fontSize: 12,
    backgroundColor: "#1976d2",
    boxShadow: "none",
    cursor: isDisabledInterest ? "not-allowed" : "pointer",

    "&:hover": {
      backgroundColor: "#1976d2", // ✅ keep same blue
      boxShadow: "none",
    },

    "&:active": {
      backgroundColor: "#1976d2",
      boxShadow: "none",
    },

    "&.Mui-focusVisible": {
      backgroundColor: "#1976d2",
    },

    "&.Mui-disabled": {
      backgroundColor: "#CBD5E1",
      color: "#64748B",
      cursor: "not-allowed",
    },
  }}
>
  {loading ? "SUBMITTING..." : "INTERESTED"}
</Button>

      ) : isPaid ? (
        <Box
          sx={{
            width: ACTION_WIDTH,
            height: 32,
            fontSize: 11,
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: STATUS_CONFIG[status]?.bg,
            color: STATUS_CONFIG[status]?.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#E8F5E9",
              boxShadow: "0 2px 8px rgba(13, 238, 43, 0.2)",
            },
          }}
        >
          {STATUS_CONFIG[status]?.label}
        </Box>
      )
       : isBooked ? (
        <Box
          onClick={() => onBookedClick && onBookedClick(domain, week)}
          sx={{
            width: ACTION_WIDTH,
            height: 32,
            fontSize: 11,
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: STATUS_CONFIG[status]?.bg,
            color: STATUS_CONFIG[status]?.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#F8D7DA",
              boxShadow: "0 2px 8px rgba(211, 47, 47, 0.2)",
            },
          }}
        >
          {STATUS_CONFIG[status]?.label}
        </Box>
      ) : isPaymentPending ? (
          <Box
            onClick={() => onBookedClick && onBookedClick(domain, week)}
            sx={{
              width: ACTION_WIDTH, height: 32, fontSize: 11, fontWeight: 600,
              borderRadius: "8px",
              backgroundColor: STATUS_CONFIG.payment_pending.bg,
              color: STATUS_CONFIG.payment_pending.color,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#FFE0B2",
                boxShadow: "0 2px 8px rgba(245,127,23,0.3)",
              },
            }}
          >
            PAY NOW
          </Box>

        ) : (
        <Box
          sx={{
            width: ACTION_WIDTH,
            height: 32,
            fontSize: 11,
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: STATUS_CONFIG[status]?.bg,
            color: STATUS_CONFIG[status]?.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {STATUS_CONFIG[status]?.label}
        </Box>
      )}
    </Box>
  );
};

/* ---------- Date Formatter ---------- */
const formatWeekRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const ordinal = (n) =>
    n > 3 && n < 21 ? "th" : ["st", "nd", "rd"][(n % 10) - 1] || "th";

  return `${start.getDate()}${ordinal(start.getDate())}–${end.getDate()}${ordinal(
    end.getDate(),
  )} ${start.toLocaleString("en-US", { month: "short" })}`;
};

  const MONTHS = [
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 },
  ];
/* ---------- Main Component ---------- */
export default function RequestProgram({ type = "fdp", defaultView = "upcoming", hideToggle = false }) {
  const token = localStorage.getItem("accessToken");

  const [rawData, setRawData] = useState({});
  const [loading, setLoading] = useState(false);
  const [submittingKey, setSubmittingKey] = useState(null);
  const [page, setPage] = useState(1);
  //ADDED FOR TECH CAMP INTERESTED BUTTON CLICK
  const [popup_status, setPopupStatus] = useState(null);
  const [after_date, setAfterDate] = useState(null);

  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewType, setViewType] = useState(defaultView);

  const [warningMessage, setWarningMessage] = useState(null);
  const [openTC, setOpenTC] = useState(false);
  const [agreeTC, setAgreeTC] = useState(false);
  const [pendingInterest, setPendingInterest] = useState(null);
  const [openBookedDetails, setOpenBookedDetails] = useState(false);
  const [bookedWeekInfo, setBookedWeekInfo] = useState(null);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1–12

  const MONTHS_PER_PAGE = viewType === "completed" ? 4 : 3;
  // const currentYear = new Date().getFullYear();

  /* ---------- YEAR OPTIONS ---------- */
  const YEARS = useMemo(() => {
    if (viewType !== "upcoming") {
      // For completed/archive view, show all past years till current
      const years = [];
      for (let y = 2021; y <= currentYear; y++) years.push(y);
      return years;
    }

    // Upcoming view
    // Before September → only current year
    if (currentMonth < 9) {
      return [currentYear];
    }

    // September onwards → current year + next year
    return [currentYear, currentYear + 1];
  }, [viewType, currentYear, currentMonth]);

  /* ---------- FETCH DATA ---------- */

  const fetchAvailability = useCallback(
    async (monthValue = "") => {
      try {
        setLoading(true);

        let payload = {};
        if (monthValue) {
          const [year, month] = monthValue.split("-");
          payload = { year: Number(year), month: Number(month) };
        }

        const url =
          viewType === "completed"
            ? `${BASE_URL}${API_MAP[type].past}`
            : `${BASE_URL}${API_MAP[type].availability}`;

        const res = await axios.post(url, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (viewType === "upcoming" ) {
          setRawData(res.data?.data || {});
          setWarningMessage(res.data?.warning_message || null);
          // ✅ NEW
          setPopupStatus(res.data?.popup_status ?? null);
          setAfterDate(res.data?.after_date ?? null);
        } else {
          setRawData(res.data || {});
          setWarningMessage(null);
        }

        setPage(1);
      } catch (e) {
        setRawData({});
        setWarningMessage(null);
      } finally {
        setLoading(false);
      }
    },
    [token, viewType],
  );

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  /* ---------- SUBMIT ---------- */
  const actuallySubmitInterest = async (domain, week) => {
    const key = `${domain.domain_id}-${domain.trainer_id}-${week.start_date}-${week.end_date}`;
    try {
      setSubmittingKey(key);

      const res = await axios.post(
        `${BASE_URL}${API_MAP[type].submit}`,
        {
          trainer_id: domain.trainer_id,
          domain_id: domain.domain_id,
          start_date: week.start_date,
          end_date: week.end_date,
          is_paid: 1,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
     
      // only for Tech Camp
      if (type === "tech_camp") {
        alert(res?.data?.message || "TechCamp interest submitted successfully");
      }
      // Refresh data after success
      fetchAvailability();
    } catch (e) {
      alert(
        e?.response?.data?.detail ||
          e?.response?.data?.error ||
          "Something went wrong",
      );
    } finally {
      setSubmittingKey(null);
    }
  };
// ADDED NEW FEATURE FOR TECH CAMP POPUP
const shouldShowPopup = (weekStartDate) => {
  if (type !== "tech_camp") return false;

  // Condition 1: popup_status === null && after_date === null → NO POPUP
  if (popup_status === null && after_date === null) {
    return false;
  }

  // Condition 2: popup_status === "every_click" && after_date === null → SHOW POPUP
  if (popup_status === "every_click" && after_date === null) {
    return true;
  }

  // Condition 3: popup_status === "only_date" && after_date !== null
  // Check if week start_date is on or before after_date
  if (popup_status === "only_date" && after_date !== null) {
    // Show popup only if week start_date <= after_date
    return weekStartDate <= after_date;
  }

  return false;
};

const getPopupMessage = () => {
  if (type !== "tech_camp") return warningMessage;

  // Condition 2 → every_click (Lite Membership)
  if (popup_status === "every_click" && after_date === null) {
    return (
      "Colleges with Lite Membership are not eligible to host Tech Camp under the subscription.\n" +
      "They may conduct the program under the following charges\n" +
      "Tech Camp Charges : ₹10,000 + GST per Tech Camp\n" +
      "Maximum Students : 60–80 per Tech Camp.\n\n" 
    );
  }

  // Condition 3 → only_date (Basic member used 1 free slot)
  if (popup_status === "only_date" && after_date !== null) {
    return (
      "You have used your free Tech Camp limit. " +
      "You may conduct the program under the following charges\n" +
      "Tech Camp Charges : ₹10,000 + GST per Tech Camp\n" +
      "Maximum Students : 60–80 per Tech Camp.\n\n" +
      `Your free Tech Camp will be available after ${after_date}.`
    );
  }

  // fallback → API warning message
  return warningMessage;
};

const submitInterest = (domain, week) => {
  // If status is NOT available, direct submit without popup
  if (domain.status !== "available") {
    actuallySubmitInterest(domain, week);
    return;
  }

  // ✅ NEW: If ANY domain in this week is not "available", direct submit
  const weekHasNonAvailable = week.domains.some(d => d.status !== "available");
  if (weekHasNonAvailable) {
    actuallySubmitInterest(domain, week);
    return;
  }

  // Condition 1: popup_status === null && after_date === null → Direct submit, NO POPUP
  if (popup_status === null && after_date === null) {
    actuallySubmitInterest(domain, week);
    return;
  }

  // Condition 3: popup_status === "only_date" && after_date !== null
  // If week start_date is after after_date, direct submit
  if (popup_status === "only_date" && after_date !== null) {
    if (week.start_date > after_date) {
      // Week is after the after_date, direct submit
      actuallySubmitInterest(domain, week);
      return;
    }
  }

  // Condition 2 & 3: Show popup if conditions are met
  if (shouldShowPopup(week.start_date)) {
    setPendingInterest({ domain, week });
    setOpenTC(true);
    return;
  }

  // Normal warning popup (FDP + fallback)
  if (warningMessage) {
    setPendingInterest({ domain, week });
    setOpenTC(true);
    return;
  }

  // Direct submit
  actuallySubmitInterest(domain, week);
};

const handleBookedClick = (domain, week) => {
  // Open popup with booked details
  setBookedWeekInfo({
    domain_name: domain.domain_name,
    week_no: week.week_no,
    start_date: week.start_date,
    end_date: week.end_date,
    trainer_id: domain.trainer_id,
    domain_id: domain.domain_id,
    status: domain.status,
  });
  setOpenBookedDetails(true);
};

  /* ---------- FLATTEN ---------- */
  const flattenedMonths = useMemo(() => {
    const rows = [];

    // Sort years
    const sortedYears = Object.keys(rawData)
      .map(Number)
      .sort((a, b) => (viewType === "upcoming" ? a - b : b - a)); // ascending for upcoming, descending for completed

    sortedYears.forEach((year) => {
      const months = rawData[year];

      // Sort months
      const sortedMonths = Object.keys(months).sort((a, b) => {
        const aIndex = MONTHS.findIndex((m) => m.label === a);
        const bIndex = MONTHS.findIndex((m) => m.label === b);
        return viewType === "upcoming" ? aIndex - bIndex : bIndex - aIndex;
      });

      sortedMonths.forEach((month) => {
        // Optional: sort weeks inside month descending/ascending
        const monthData = { ...months[month] };
        monthData.weeks.sort((w1, w2) => {
          const d1 = new Date(w1.start_date);
          const d2 = new Date(w2.start_date);
          return viewType === "upcoming" ? d1 - d2 : d2 - d1;
        });

        rows.push({ year, month, data: monthData });
      });
    });

    return rows;
  }, [rawData, viewType]);

  const totalPages = Math.ceil(flattenedMonths.length / MONTHS_PER_PAGE);
  const paginated = flattenedMonths.slice(
    (page - 1) * MONTHS_PER_PAGE,
    page * MONTHS_PER_PAGE,
  );
  const isLastPage = page === totalPages;

  const selectedMonthLabel = useMemo(() => {
    if (!filterYear || !filterMonth) return "";

    const monthName = MONTHS.find(
      (m) => m.value === Number(filterMonth),
    )?.label;

    return `${monthName?.toUpperCase()} ${filterYear}`;
  }, [filterMonth, filterYear]);

  return (
    <Box p={3}>
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" gap={1} alignItems="center">
          {/* FILTER BUTTON */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<CalendarMonth />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "uppercase",
            }}
          >
            {selectedMonthLabel ? `FILTER: ${selectedMonthLabel}` : "FILTER"}
          </Button>

          {/* CLEAR FILTER */}
          {selectedMonthLabel && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setFilterYear("");
                setFilterMonth("");
                fetchAvailability();
              }}
              sx={{
                fontWeight: 600,
                borderRadius: "8px",
                color: "#22C55E",
                borderColor: "#22C55E",
                "&:hover": {
                  backgroundColor: "#ECFDF5",
                  borderColor: "#16A34A",
                },
              }}
            >
              CLEAR FILTER
            </Button>
          )}
        </Box>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Paper sx={{ p: 2, width: 260 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Year"
              value={filterYear}
              onChange={(e) => {
                setFilterYear(e.target.value);
                setFilterMonth("");
              }}
              sx={{ mb: 2 }}
            >
              {YEARS.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Month"
              value={filterMonth}
              disabled={!filterYear}
              onChange={(e) => setFilterMonth(e.target.value)}
              sx={{ mb: 2 }}
            >
              {MONTHS.filter((m) => {
                if (viewType !== "upcoming") return true;

                // current year → current month onwards
                if (Number(filterYear) === currentYear) {
                  return m.value >= currentMonth;
                }

                // next year → all months
                if (Number(filterYear) === currentYear + 1) {
                  return true;
                }

                return false;
              }).map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>

            <Button
              fullWidth
              size="small"
              variant="outlined"
              disabled={!filterYear || !filterMonth}
              onClick={() => {
                fetchAvailability(
                  `${filterYear}-${String(filterMonth).padStart(2, "0")}`,
                );
                setAnchorEl(null);
              }}
            >
              APPLY
            </Button>
          </Paper>
        </Popover>

        {!hideToggle && (
          <Box sx={{ display: "flex", background: "#F1F5F9", borderRadius: 2 }}>
            <Button
              size="small"
              onClick={() => {
                setViewType("upcoming");
                setFilterYear(""); // reset filters
                setFilterMonth("");
                setPage(1); // reset pagination
              }}
              sx={{
                px: 3,
                color: viewType === "upcoming" ? "#fff" : "#475569",
                backgroundColor:
                  viewType === "upcoming" ? "#1976d2" : "transparent",
              }}
            >
              Available
            </Button>
            <Button
              size="small"
              onClick={() => {
                setViewType("completed");
                setFilterYear(""); // reset filters
                setFilterMonth("");
                setPage(1);
              }}
              sx={{
                px: 3,
                color: viewType === "completed" ? "#fff" : "#475569",
                backgroundColor:
                  viewType === "completed" ? "#1976d2" : "transparent",
              }}
            >
              Archive
            </Button>
          </Box>
        )}
      </Box>

      {/* DATA */}
      <Box sx={{ position: "relative" }}>
        {/* 🔄 TAB-LEVEL LOADER */}
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
              backgroundColor: "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
            }}
          >
            <CircularProgress size={36} />
          </Box>
        )}
        <Paper elevation={3} sx={{ mt: 3 }}>
          {/* ❌ Show message if no data */}
          {!loading && flattenedMonths.length === 0 && (
            <Box py={6} display="flex" justifyContent="center">
              <Typography fontWeight={600} fontSize={14} textAlign="center">
                {viewType === "upcoming"
                  ? filterYear || filterMonth
                    ? "No upcoming slots available for the selected year & month."
                    : "No upcoming slots available."
                  : filterYear || filterMonth
                    ? "No completed slots found for the selected year & month."
                    : "No completed slots found."}
              </Typography>
            </Box>
          )}

          {/* ✅ Render FDP weeks if data exists */}
          {flattenedMonths.length > 0 &&
            paginated.map(({ year, month, data }) => (
              <Box
                key={`${year}-${month}`}
                className="grid grid-cols-12 border-b"
              >
                <Box className="col-span-2 bg-gray-50 flex justify-center items-center border-r">
                  <Typography fontSize={13} fontWeight={600}>
                    {month} {year}
                  </Typography>
                </Box>

                <Box className="col-span-10">
                  {data.weeks.map((week) => (
                    <Box
                      key={week.start_date}
                      className="grid grid-cols-12"
                      sx={{
                        borderBottom: "1px solid #E5E7EB", // horizontal line
                      }}
                    >
                      {/* WEEK COLUMN */}
                      <Box
                        className="col-span-3 flex justify-center items-center"
                        sx={{
                          borderRight: "1px solid #E5E7EB", // vertical line
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          <Typography fontSize={13}>
                            Week {week.week_no}
                          </Typography>
                          <Typography fontSize={11}>
                            {formatWeekRange(week.start_date, week.end_date)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* DOMAINS COLUMN */}
                      <Box
                        className="col-span-9"
                        sx={{ display: "flex", flexDirection: "column" }}
                      >
                        {week.domains.map((d, i) => {
                          const key = `${d.domain_id}-${d.trainer_id}-${week.start_date}-${week.end_date}`;
                          return (
                            <DomainRow
                              key={key}
                              domain={d}
                              week={week}
                              loading={submittingKey === key}
                              onSubmit={() => submitInterest(d, week)}
                              onBookedClick={handleBookedClick}
                              showDivider={i !== week.domains.length - 1} // keep horizontal lines
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
        </Paper>
        {/* 🧾 END OF ARCHIVE MESSAGE */}
        {viewType === "completed" &&
          isLastPage &&
          flattenedMonths.length > 0 && (
            <Box
              mt={3}
              py={2}
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                backgroundColor: "#F8FAFC",
                borderRadius: 2,
                border: "1px dashed #CBD5E1",
              }}
            >
              <Typography
                fontSize={13}
                color="text.secondary"
                fontWeight={500}
                textAlign="center"
              >
                These are the completed FDP slots of current and last year to
                view more use filter.
              </Typography>
            </Box>
          )}
      </Box>
      {/* PAGINATION */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="small"
          />
        </Box>
      )}

      {/* TERMS & CONDITIONS MODAL */}
      <Dialog
        open={openTC}
        onClose={() => setOpenTC(false)}
        maxWidth="sm" // 🔽 reduced from md
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 4,
            minHeight: "55vh", // 🔽 reduced height
            maxHeight: "65vh",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "#fff",
            fontWeight: 600,
            fontSize: 14,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            pb: 3.5,
            pt: 3,
            px: 3,
            borderRadius: "8px 8px 0 0",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 18,
              flexShrink: 0,
              border: "2px solid rgba(255, 255, 255, 0.4)",
            }}
          >
            ℹ
          </Box>
          <Box>
            <Typography fontSize={15} fontWeight={700} lineHeight={1.6} sx={{ color: "#fff" }}>
              Important Information
            </Typography>
          </Box>
        </DialogTitle>

        {/* Content */}
        <DialogContent
          sx={{
            py: 4,
            px: 4,
            pt: 3.5,
            maxHeight: "55vh",
            overflowY: "auto",
            background: "linear-gradient(to bottom, #FAFBFC 0%, #F5F7FA 100%)",
          }}
        >
{/* 
          <Box
            component="ul"
            sx={{
              pl: 3,
              m: 0,
              listStyleType: "disc",
              "& li": {
                display: "list-item",
                mb: 1,
              },
            }}
          > */}
          <Box
    component="ul"
    sx={{
      pl: 3.5,
      mt: 1.5,
      mb: 0,
      listStyleType: "disc",
      "& li": {
        display: "list-item",
        mb: 1.8,
        color: "#334155",
        lineHeight: 1.7,
      },
      "& li::marker": {
        color: "#1976d2",
        fontWeight: "bold",
      },
    }}
  >
            {(getPopupMessage() || "").split("\n").map((line, index)  => {
              const trimmedLine = line.trim();

              // Skip empty lines
              if (!trimmedLine) return null;

              // 👉 Render OR without bullet
              if (trimmedLine.toUpperCase() === "OR") {
                return (
                  <Box
                    key={index}
                    sx={{
                      textAlign: "center",
                      fontWeight: 600,
                      my: 1,
                    }}
                  >
                    <Typography fontSize={14}>OR</Typography>
                  </Box>
                );
              }

              // 👉 Normal bullet points
              return (
                <li key={index}>
                  <Typography fontSize={14} lineHeight={1.6}>
                    {line}
                  </Typography>
                </li>
              );
            })}
          </Box>


        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #E5E7EB",
            backgroundColor: "#FAFAFA",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setOpenTC(false);
              setPendingInterest(null);
            }}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setOpenTC(false);
              actuallySubmitInterest(
                pendingInterest.domain,
                pendingInterest.week,
              );
              setPendingInterest(null);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              px: 3,
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* BOOKED DETAILS DIALOG */}
      {bookedWeekInfo?.status === "payment_pending" && (
      <Dialog
        open={openBookedDetails}
        onClose={() => setOpenBookedDetails(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 4,
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            background: "#4077f0",
            color: "#fff",
            fontWeight: 600,
            fontSize: 16,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            pb: 3,
            pt: 3,
            px: 3,
            borderRadius: "8px 8px 0 0",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 18,
              flexShrink: 0,
              border: "2px solid rgba(255, 255, 255, 0.4)",
            }}
          >
            {bookedWeekInfo?.status === "payment_pending" ? "💳" : "⏳"}
          </Box>
          <Box>
            <Typography fontSize={16} fontWeight={700} sx={{ color: "#fff" }}>
              Payment Details
            </Typography>
          </Box>
        </DialogTitle>

        {/* Content */}
      <DialogContent
    sx={{
    py: 4,
    px: 4,
    pt: 3.5,
    background: "linear-gradient(to bottom, #FAFBFC 0%, #F5F7FA 100%)",
  }}
>
  {/* Booking Details */}
<Box sx={{ mb: 3 }}>
  <Typography
    fontSize={13}
    fontWeight={600}
    color="#475569"
    sx={{ mb: 1.5 }}
  >
  </Typography>

  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      border: "1px solid #E5E7EB",
      backgroundColor: "#FFFFFF",
    }}
  >
    {/* Header Row */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        columnGap: 2,
        mb: 1,
      }}
    >
      <Typography fontSize={12} color="#94A3B8">
        Tech Camp Domain
      </Typography>
      <Typography fontSize={12} color="#94A3B8">
        Tech Camp Duration
      </Typography>
    </Box>

    {/* Value Row */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        columnGap: 2,
      }}
    >
      <Typography
        fontSize={14}
        fontWeight={600}
        color="#1E293B"
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {bookedWeekInfo?.domain_name}
      </Typography>

      <Typography fontSize={14} fontWeight={600} color="#1E293B">
        {bookedWeekInfo?.start_date} to {bookedWeekInfo?.end_date}
      </Typography>
    </Box>
  </Box>
</Box>

  {/* Payment Details */}
  <Box sx={{ mb: 2 }}>
    <Typography
      fontSize={13}
      fontWeight={600}
      color="#475569"
      sx={{ mb: 1.5 }}
    >
      Bank Account Details
    </Typography>

    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid #E5E7EB",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography fontSize={12} color="#94A3B8">
          Account Name
        </Typography>
        <Typography fontSize={14} fontWeight={600}>
          EDUSKILLS FOUNDATION
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography fontSize={12} color="#94A3B8">
          Bank Name
        </Typography>
        <Typography fontSize={14} fontWeight={600}>
          AXIS BANK
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography fontSize={12} color="#94A3B8">
          Account Number
        </Typography>
        <Typography fontSize={14} fontWeight={600} sx={{ fontFamily: "monospace" }}>
          922010056667723
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography fontSize={12} color="#94A3B8">
          IFSC Code
        </Typography>
        <Typography fontSize={14} fontWeight={600} sx={{ fontFamily: "monospace" }}>
          UTIB0000381
        </Typography>
      </Box>

      {/* Payment Summary */}
<Box sx={{ mt: 2 }}>
  <Typography fontSize={12} color="#94A3B8" sx={{ mb: 1 }}>
    Payment Summary
  </Typography>

  <Box
    sx={{
      borderRadius: 1.5,
      border: "1px solid #E5E7EB",
      overflow: "hidden",
    }}
  >
    {/* Program Fee Row */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        py: 1.2,
        borderBottom: "1px solid #F1F5F9",
      }}
    >
      <Typography fontSize={13} color="#475569">
        Program Fee
      </Typography>
      <Typography fontSize={13} fontWeight={600} color="#1E293B">
        ₹10,000
      </Typography>
    </Box>

    {/* GST Row */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        py: 1.2,
        borderBottom: "1px solid #F1F5F9",
      }}
    >
      <Typography fontSize={13} color="#475569">
        Taxes (18% GST)
      </Typography>
      <Typography fontSize={13} fontWeight={600} color="#1E293B">
        ₹1,800
      </Typography>
    </Box>

    {/* Total Row */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        py: 1.4,
        backgroundColor: "#FEF3C7",
      }}
    >
      <Typography fontSize={13} fontWeight={700} color="#92400E">
        Total Payable Amount
      </Typography>
      <Typography fontSize={15} fontWeight={800} color="#DC2626">
        ₹11,800
      </Typography>
    </Box>
  </Box>
</Box>
{/* Note */}
<Box
  sx={{
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    mt: 2,
    p: 1.5,
    borderRadius: 1.5,
    backgroundColor: "#FFF7ED",
    border: "1px solid #FED7AA",
  }}
>
  <Typography fontSize={16} sx={{ lineHeight: 1, mt: "1px" }}>
    📌
  </Typography>
  <Typography fontSize={12} color="#92400E" fontWeight={500}>
    <b>Note:</b> Your slot will be confirmed after payment verification.
  </Typography>
</Box>
    </Box>
  </Box>
</DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: "2px solid #E0E7FF",
            backgroundColor: "#F8FAFC",
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setOpenBookedDetails(false);
              setBookedWeekInfo(null);
            }}
            sx={{ 
              textTransform: "none", 
              fontWeight: 600,
              borderColor: "#CBD5E1",
              color: "#475569",
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>)}
    </Box>
  );
}